// @flow
// Provides a function that will restrict execution of a promise returning
// function to one at a time. Pass a key to lock to that key.

const waiting = {};

const isLocked = key => key in waiting && waiting[key].length > 0;

const lock = key => {
  return new Promise(resolve => {
    if (!waiting[key]) {
      waiting[key] = [];
    }
    waiting[key].push(resolve);
    if (waiting[key].length === 1) {
      resolve();
    }
  });
};

const release = key => {
  setTimeout(() => {
    waiting[key].shift();
    if (waiting[key].length > 0) {
      waiting[key][0]();
    } else {
      delete waiting[key];
    }
  }, 0);
};

const prepareWork = (key, fn) => {
  // If we're locked, then our work should be lazy.
  if (isLocked(key)) {
    return () => fn();
  }
  // Otherwise, we should kick off the work right now. Don't wait for the next
  // tick, or we might get unexpected ordering.
  const result = fn();
  // Our work just returns the result.
  return () => result;
};

const lockPromise = (key: string, fn: Function) => {
  const doWork = prepareWork(key, fn);

  return lock(key)
    .then(doWork)
    .then(result => {
      release(key);
      return result;
    })
    .catch(err => {
      release(key);
      throw err;
    });
};

export default lockPromise;
